/**
 * @typedef {{
 *   id: string;
 *   source: string;
 *   worldX: number;
 *   worldY: number;
 *   baseWidthMeters: number;
 *   baseHeightMeters: number;
 *   scale: number;
 *   rotationDegrees: 0 | 90 | 180 | 270;
 *   opacity: number;
 *   visible: boolean;
 * }} BackgroundReferenceImage
 */

import { getFloorPlan2DBounds } from './floorPlanContentBounds.js';
import { nextDesignerId } from './floorPlanDesignerState.js';

const ROTATIONS = [0, 90, 180, 270];

export const MIN_BACKGROUND_SCALE = 0.01;
export const MAX_BACKGROUND_SCALE = 100;
export const DEFAULT_BACKGROUND_SCALE = 1;

/**
 * @param {number} value
 * @returns {number}
 */
export function clampBackgroundScale(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return DEFAULT_BACKGROUND_SCALE;
  return Math.max(MIN_BACKGROUND_SCALE, Math.min(MAX_BACKGROUND_SCALE, parsed));
}

/**
 * @param {Partial<BackgroundReferenceImage> | null | undefined} background
 * @returns {{ widthMeters: number; heightMeters: number }}
 */
export function getBackgroundRenderedSize(background) {
  if (!background) return { widthMeters: 0, heightMeters: 0 };
  const scale = clampBackgroundScale(background.scale ?? DEFAULT_BACKGROUND_SCALE);
  const baseWidthMeters = Number(background.baseWidthMeters);
  const baseHeightMeters = Number(background.baseHeightMeters);
  if (!Number.isFinite(baseWidthMeters) || !Number.isFinite(baseHeightMeters)) {
    return { widthMeters: 0, heightMeters: 0 };
  }
  return {
    widthMeters: baseWidthMeters * scale,
    heightMeters: baseHeightMeters * scale,
  };
}

/**
 * @param {Record<string, unknown> | null | undefined} background
 * @param {number} [metersPerPixel]
 * @returns {BackgroundReferenceImage | null}
 */
export function normalizeBackgroundReferenceImage(background, metersPerPixel = 0.01) {
  if (!background || typeof background !== 'object') return null;
  const mpp = Number.isFinite(metersPerPixel) && metersPerPixel > 0 ? metersPerPixel : 0.01;

  let baseWidthMeters = Number(background.baseWidthMeters);
  let baseHeightMeters = Number(background.baseHeightMeters);
  let scale = clampBackgroundScale(background.scale ?? DEFAULT_BACKGROUND_SCALE);
  let worldX = Number(background.worldX);
  let worldY = Number(background.worldY);

  if (!Number.isFinite(baseWidthMeters) || !Number.isFinite(baseHeightMeters)) {
    const legacyWidth = Number(background.widthMeters);
    const legacyHeight = Number(background.heightMeters);
    if (!Number.isFinite(legacyWidth) || !Number.isFinite(legacyHeight)) return null;
    baseWidthMeters = legacyWidth;
    baseHeightMeters = legacyHeight;
    scale = DEFAULT_BACKGROUND_SCALE;
    if (Number.isFinite(worldX) && Number.isFinite(worldY)) {
      const widthPx = legacyWidth / mpp;
      const heightPx = legacyHeight / mpp;
      worldX += widthPx / 2;
      worldY += heightPx / 2;
    }
  }

  if (!Number.isFinite(worldX) || !Number.isFinite(worldY)) {
    return null;
  }

  return {
    id: String(background.id || nextDesignerId('bg_ref')),
    source: String(background.source || ''),
    worldX,
    worldY,
    baseWidthMeters,
    baseHeightMeters,
    scale,
    rotationDegrees: normalizeBackgroundRotation(background.rotationDegrees),
    opacity: Math.max(0, Math.min(1, Number(background.opacity) || 0)),
    visible: background.visible !== false,
  };
}

/**
 * @param {number} value
 * @returns {0 | 90 | 180 | 270}
 */
export function normalizeBackgroundRotation(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  const normalized = ((Math.round(parsed / 90) * 90) % 360 + 360) % 360;
  return /** @type {0 | 90 | 180 | 270} */ (ROTATIONS.includes(normalized) ? normalized : 0);
}

/**
 * @param {import('./floorPlanDesignerState.js').DesignerState} state
 * @param {File} file
 * @param {{ naturalWidth: number; naturalHeight: number; metersPerPixel: number }} dimensions
 * @returns {Promise<import('./floorPlanDesignerState.js').DesignerState>}
 */
export async function loadBackgroundReferenceImage(state, file, dimensions) {
  const source = URL.createObjectURL(file);
  const baseWidthMeters = dimensions.naturalWidth * dimensions.metersPerPixel;
  const baseHeightMeters = dimensions.naturalHeight * dimensions.metersPerPixel;
  const drawingBounds = getFloorPlan2DBounds({ state, includeBackground: false });
  const centerX = drawingBounds
    ? (drawingBounds.minX + drawingBounds.maxX) / 2
    : state.canvasWidth / 2;
  const centerY = drawingBounds
    ? (drawingBounds.minY + drawingBounds.maxY) / 2
    : state.canvasHeight / 2;

  return {
    ...state,
    backgroundReferenceImage: normalizeBackgroundReferenceImage(
      {
        id: nextDesignerId('bg_ref'),
        source,
        worldX: centerX,
        worldY: centerY,
        baseWidthMeters,
        baseHeightMeters,
        scale: DEFAULT_BACKGROUND_SCALE,
        rotationDegrees: 0,
        opacity: 0.65,
        visible: true,
      },
      dimensions.metersPerPixel,
    ),
  };
}

/**
 * @param {import('./floorPlanDesignerState.js').DesignerState} state
 * @param {Partial<BackgroundReferenceImage>} patch
 */
export function updateBackgroundReferenceImage(state, patch) {
  const current = normalizeBackgroundReferenceImage(
    state.backgroundReferenceImage,
    state.scale?.metersPerPixel,
  );
  if (!current) return state;

  const next = { ...current };

  if (patch.worldX !== undefined || patch.worldY !== undefined) {
    const worldX = patch.worldX !== undefined ? Number(patch.worldX) : next.worldX;
    const worldY = patch.worldY !== undefined ? Number(patch.worldY) : next.worldY;
    if (!Number.isFinite(worldX) || !Number.isFinite(worldY)) return state;
    next.worldX = worldX;
    next.worldY = worldY;
  }

  if (patch.scale !== undefined) {
    next.scale = clampBackgroundScale(patch.scale);
  }

  if (patch.rotationDegrees !== undefined) {
    next.rotationDegrees = normalizeBackgroundRotation(patch.rotationDegrees);
  }

  if (patch.opacity !== undefined) {
    next.opacity = Math.max(0, Math.min(1, Number(patch.opacity) || 0));
  }

  if (patch.visible !== undefined) {
    next.visible = Boolean(patch.visible);
  }

  if (patch.source !== undefined) {
    next.source = String(patch.source);
  }

  return { ...state, backgroundReferenceImage: next };
}

/**
 * @param {import('./floorPlanDesignerState.js').DesignerState} state
 */
export function removeBackgroundReferenceImage(state) {
  if (state.backgroundReferenceImage?.source?.startsWith('blob:')) {
    URL.revokeObjectURL(state.backgroundReferenceImage.source);
  }
  return { ...state, backgroundReferenceImage: null };
}

/**
 * @param {import('./floorPlanDesignerState.js').DesignerState} state
 */
export function resetBackgroundReferenceImageTransform(state) {
  const current = normalizeBackgroundReferenceImage(
    state.backgroundReferenceImage,
    state.scale?.metersPerPixel,
  );
  if (!current) return state;
  const centerX = state.canvasWidth / 2;
  const centerY = state.canvasHeight / 2;
  return updateBackgroundReferenceImage(state, {
    worldX: centerX,
    worldY: centerY,
    scale: DEFAULT_BACKGROUND_SCALE,
    rotationDegrees: 0,
    opacity: 0.65,
    visible: true,
  });
}

/**
 * @param {BackgroundReferenceImage | null | undefined} background
 * @param {number} [metersPerPixel]
 */
export function exportBackgroundReferenceImage(background, metersPerPixel = 0.01) {
  const normalized = normalizeBackgroundReferenceImage(background, metersPerPixel);
  if (!normalized) return null;
  const { widthMeters, heightMeters } = getBackgroundRenderedSize(normalized);
  return {
    id: normalized.id,
    source: normalized.source,
    worldX: normalized.worldX,
    worldY: normalized.worldY,
    baseWidthMeters: normalized.baseWidthMeters,
    baseHeightMeters: normalized.baseHeightMeters,
    scale: normalized.scale,
    widthMeters,
    heightMeters,
    rotationDegrees: normalized.rotationDegrees,
    opacity: normalized.opacity,
    visible: normalized.visible,
  };
}
